import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';

import { Style, Icon, Circle as CircleStyle, Fill, Stroke } from 'ol/style.js';

import { fromLonLat } from 'ol/proj.js';
import { API_URL, POI_ICON_SIZE } from './config.js';
import { getMap } from './mapInstance.js';
import { secureFetch } from './securityUtil.js';
import { getSelectedCountryAbbreviation } from './country-language.js';

const poiColors = [
  '#ff0000',
  '#008000',
  '#0000ff',
  '#ff00ff',
  '#ffa500',
  '#00bcd4',
  '#8e24aa'
];

const poiPinSymbols = [
  '<circle cx="16" cy="16" r="6"/>',
  '<rect x="11" y="11" width="10" height="10" rx="1"/>',
  '<path d="M16 9l7 13H9z"/>',
  '<path d="M16 8l8 8-8 8-8-8z"/>',
  '<path d="M16 8l2.4 5 5.6.8-4 3.9.9 5.5-4.9-2.6-4.9 2.6.9-5.5-4-3.9 5.6-.8z"/>',
  '<path d="M13.5 9h5v4.5H23v5h-4.5V23h-5v-4.5H9v-5h4.5z"/>',
  '<path d="M11.2 9l4.8 4.8L20.8 9l3.2 3.2-4.8 4.8 4.8 4.8-3.2 3.2-4.8-4.8-4.8 4.8L8 21.8l4.8-4.8L8 12.2z"/>'
];

const poiPinBodies = [
  '<path d="M16 1C7.72 1 1 7.72 1 16c0 11.25 15 25 15 25s15-13.75 15-25C31 7.72 24.28 1 16 1z"/>',
  '<path d="M5 3h22a4 4 0 014 4v20a4 4 0 01-4 4h-7l-4 10-4-10H5a4 4 0 01-4-4V7a4 4 0 014-4z"/>',
  '<path d="M16 1a14 14 0 0114 14c0 8-6.2 13.2-10.5 16L16 41l-3.5-10C8.2 28.2 2 23 2 15A14 14 0 0116 1z"/>',
  '<path d="M16 1l14 15-14 25L2 16z"/>',
  '<path d="M3 3h26v16c0 9-6.8 15.8-13 22C9.8 34.8 3 28 3 19z"/>',
  '<path d="M16 1l13 7v17L16 41 3 25V8z"/>',
  '<path d="M4 2h24a3 3 0 013 3v23a3 3 0 01-3 3h-8l-4 10-4-10H4a3 3 0 01-3-3V5a3 3 0 013-3z"/>'
];

let poiColorIndex = 0;
const loadedPoiLayers = new Map();
const poiSetSymbolIndexes = new Map();
let nextPoiSetSymbolIndex = 0;

function nextPoiColor() {

  const c = poiColors[
    poiColorIndex % poiColors.length
  ];

  poiColorIndex++;

  return c;
}

function getPoiSetSymbolIndex(poiSetId) {
  if (!poiSetSymbolIndexes.has(poiSetId)) {
    poiSetSymbolIndexes.set(poiSetId, nextPoiSetSymbolIndex % poiPinSymbols.length);
    nextPoiSetSymbolIndex++;
  }
  return poiSetSymbolIndexes.get(poiSetId);
}

function getPoiSetId(poiSet) {
  return poiSet?.poiSetId || poiSet?.poi_set_id;
}

function isPoiSetEnabledForProject(poiSet) {
  const selected = poiSet?.project_settings?.selected;
  return selected === true || selected === 1 || selected === '1';
}

function normalizeCountry(value) {
  const country = String(value ?? '').trim().toUpperCase();
  if (country === '1' || country === 'NL' || country === 'NETHERLANDS') return 'NL';
  if (country === '4' || country === 'BE' || country === 'BELGIUM') return 'BE';
  return null;
}

function getActivePoiCountry() {
  return normalizeCountry(getSelectedCountryAbbreviation())
    || normalizeCountry(getMap()?.get?.('country'));
}

function getCategoryName(category) {
  const value = typeof category === 'string' ? category : category?.name;
  return typeof value === 'string' && value.trim() ? value.trim() : 'Unknown';
}

function getPoiCategoryName(poi) {
  const value = poi?.category_name ?? poi?.categoryName ?? poi?.category;
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') return getCategoryName(value);
  return 'Unknown';
}

function getPoiCategories(poiSet, pois) {
  const configured = Array.isArray(poiSet?.categories)
    ? poiSet.categories.map(getCategoryName)
    : [];
  const discovered = pois.map(getPoiCategoryName);

  return [...new Set([...configured, ...discovered, ...(configured.length ? [] : ['Unknown'])].filter(Boolean))];
}

function getPoiIconSrc(icon) {
  const value = icon.trim();
  if (value.startsWith('<svg')) {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(value)}`;
  }

  try {
    return new URL(value, API_URL).href;
  } catch {
    return value;
  }
}

function createDefaultPinSrc(color, pinIndex = 0, symbolIndex = 0) {
  const body = poiPinBodies[pinIndex % poiPinBodies.length];
  const symbol = poiPinSymbols[symbolIndex % poiPinSymbols.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><g fill="${color}" stroke="#ffffff" stroke-width="2" stroke-linejoin="round">${body}</g><g fill="#ffffff">${symbol}</g></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function createPoiStyle(icon, categoryColor, pinIndex = 0, symbolIndex = 0, highlighted = false) {
  const hasConfiguredIcon = typeof icon === 'string' && icon.trim();
  const src = hasConfiguredIcon
    ? getPoiIconSrc(icon)
    : createDefaultPinSrc(highlighted ? '#ff8800' : categoryColor, pinIndex, symbolIndex);
  const iconOptions = {
    anchor: [0.5, 1],
    anchorXUnits: 'fraction',
    anchorYUnits: 'fraction',
    width: POI_ICON_SIZE.width,
    height: POI_ICON_SIZE.height,
    src
  };

  const styles = [];
  if (highlighted && hasConfiguredIcon) {
    styles.push(new Style({
      image: new CircleStyle({
        radius: 13,
        fill: new Fill({ color: 'rgba(255,255,255,0.35)' }),
        stroke: new Stroke({ color: '#ff8800', width: 2 })
      })
    }));
  }
  styles.push(new Style({ image: new Icon(iconOptions) }));
  if (hasConfiguredIcon) {
    styles.push(new Style({
      image: new CircleStyle({
        radius: 4.5,
        displacement: [-11, 5],
        fill: new Fill({ color: categoryColor }),
        stroke: new Stroke({ color: '#ffffff', width: 1.5 })
      })
    }));
  }
  return styles;
}

export async function loadUserPoiLayers(userId) {

  try {
    const encodedUserId = encodeURIComponent(userId);

    const resp = await secureFetch(
      `${API_URL}users/${encodedUserId}/poi`
    );

    if (!resp.ok) {
      console.error("Unable to load POI sets");
      return;
    }

    const payload = await resp.json();
    const poiSets = Array.isArray(payload) ? payload : (payload.data || []);
    const activeCountry = getActivePoiCountry();

    if (!activeCountry) {
      console.warn('POI sets were not loaded because the active country is unavailable');
      return [];
    }

    const matchingPoiSets = poiSets.filter(set => normalizeCountry(set?.country) === activeCountry);

    await Promise.all(
      matchingPoiSets
        .filter(set => getPoiSetId(set) && set.status === 'uploaded')
        .map(set => loadPoiSet(userId, set, activeCountry))
    );

    return matchingPoiSets;

  } catch (e) {

    console.error("POI loading error", e);
  }
}

async function loadPoiSet(userId, poiSet, activeCountry = getActivePoiCountry()) {

  const poiSetId = getPoiSetId(poiSet);
  const poiSetName = typeof poiSet?.name === 'string' && poiSet.name.trim()
    ? poiSet.name.trim()
    : 'Unnamed POI Set';
  if (loadedPoiLayers.has(poiSetId)) return loadedPoiLayers.get(poiSetId);

  const resp = await secureFetch(
    `${API_URL}users/${encodeURIComponent(userId)}/poi/${encodeURIComponent(poiSetId)}`
  );

  if (!resp.ok) return;

  const payload = await resp.json();
  const pois = Array.isArray(payload) ? payload : (payload.data || []);

  if (!Array.isArray(pois)) {
    console.error(`Invalid POI response for set ${poiSetId}`);
    return;
  }

  const matchingPois = pois.filter(poi => normalizeCountry(poi?.country) === activeCountry);
  if (!matchingPois.length) return [];

  const categoryNames = getPoiCategories(poiSet, matchingPois);
  const initiallyVisible = isPoiSetEnabledForProject(poiSet);
  const poiSetSymbolIndex = getPoiSetSymbolIndex(poiSetId);
  const layers = categoryNames.map((categoryName, categoryIndex) => {
    const color = nextPoiColor();
    const symbolIndex = categoryIndex;
    const features = matchingPois
      .filter(poi => getPoiCategoryName(poi) === categoryName)
      .filter(poi => Number.isFinite(Number(poi.longitude)) && Number.isFinite(Number(poi.latitude)))
      .map(poi => {

        const feature = new Feature({

          geometry: new Point(
            fromLonLat([
              Number(poi.longitude),
              Number(poi.latitude)
            ])
          ),

          name: poi.name,
          identifier: poi.identifier,
          street: poi.street,
          country: poi.country,
          postalCode: poi.postal_code ?? poi.postalCode,
          houseNumber: poi.house_number ?? poi.houseNumber,
          category: categoryName,
          _poiLocationVisible: initiallyVisible,
          poiSetId
        });

        if (!initiallyVisible) feature.setStyle(() => null);
        return feature;
      });

    const source = new VectorSource({ features });

    const layer = new VectorLayer({
      source,
      visible: initiallyVisible,
      zIndex: 20000
    });

    layer.set('title', categoryName);
    layer.set('group', 'ownData');
    layer.set('poiCategory', categoryName);
    layer.set('poiSetId', poiSetId);
    layer.set('poiSetName', poiSetName);
    layer.set('poiStyleColor', color);
    layer.set('poiHasConfiguredIcon', typeof poiSet.icon === 'string' && !!poiSet.icon.trim());
    layer.set(
      'poiLegendIconSrc',
      typeof poiSet.icon === 'string' && poiSet.icon.trim()
        ? getPoiIconSrc(poiSet.icon)
        : createDefaultPinSrc(color, poiSetSymbolIndex, symbolIndex)
    );
    layer.setStyle(createPoiStyle(poiSet.icon, color, poiSetSymbolIndex, symbolIndex));
    layer.set('poiFeatureInfoStyle', createPoiStyle(poiSet.icon, color, poiSetSymbolIndex, symbolIndex, true));

    return layer;
  });

  const map = getMap();
  if (window.own_map?.getLayers) {
    window.own_map.setVisible(true);
    window.own_map.setZIndex(20000);
    layers.forEach(layer => window.own_map.getLayers().push(layer));
  } else {
    layers.forEach(layer => map?.addLayer(layer));
  }

  loadedPoiLayers.set(poiSetId, layers);

  document.dispatchEvent(
    new Event('base-layers-updated')
  );

  return layers;
}
